import _ from 'lodash';
// @mui
import { Stack } from '@mui/system';
import { Box, Chip, Collapse, Typography } from '@mui/material';
//components
import FormCreator from 'components/FormCreator';
import { fieldType, invalidFields, fieldsGroupType } from 'components/FormCreator/types';
//reducers
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getNewPay, upsertPayBeneficiary } from 'state/reducers/pay.reducers';
import { getApprovedBeneficiaries } from 'state/reducers/beneficiary.reducers';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
//sections
import { FormCard } from 'theme/elements/Forms';
import { FormHeader1 } from 'theme/elements/Headers';
//constants
import { PayEnum, Pay } from 'constants/pay.constants';
import { CenteredText } from 'theme/elements/Content';
import { useNavigate, useParams } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { LightGreenCard } from 'theme/elements/Cards';
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  BeneficiaryDetailsBankAccount,
  paymentsMethodsDisplayList,
  paymentsMethodsMapping,
} from 'constants/beneficiary.constants';
import { Paragraph3, Paragraph5 } from 'theme/elements/Paragraphs';
import { getCountries, getCurrencies } from 'state/reducers/global.reducers';
import { togglePage } from 'state/reducers/ui.reducers';
import { CustomLink } from 'theme/elements/Text';
import { getCompanyBalances } from 'state/reducers/balance.reducers';
import { getFormattedNumber } from 'utils';

export default function BeneficiaryDetailsForm(props: {
  handleChange: any;
  invalidFields: invalidFields;
  insufficientBalance?: boolean;
}) {
  const t = useAppSelector(selectTranslations);
  const pay: Pay = useAppSelector(getNewPay);
  const prefixFields = 'page.pay.create.step.1.beneficiary_details.beneficiary_summary.';
  const beneficiaries = useAppSelector(getApprovedBeneficiaries);
  const Countries = _.groupBy(useAppSelector(getCountries), 'id');
  const Currencies = _.groupBy(useAppSelector(getCurrencies), 'id');
  const dispatch = useAppDispatch();
  const companyBalancesList: any[] = useAppSelector(getCompanyBalances);
  const navigate = useNavigate();
  const { id } = useParams();
  const currenciesArray = useAppSelector(getCurrencies);
  const [selectedValue, setSelectedValue] = useState(pay?.[PayEnum.beneficiaryId] || '');

  useEffect(() => {
    setSelectedValue(pay?.[PayEnum.beneficiaryId] || '');
  }, [pay, id]); 
  
  const getBNECurrencySymbol = (relevantCurrency, beneficiary, fullBNEObject) => {
    if (relevantCurrency) {
      return fullBNEObject
        ? { ...beneficiary, amount: relevantCurrency?.amount, symbol: relevantCurrency?.currency?.symbol }
        : relevantCurrency;
    } else {
      const currencySymbol = currenciesArray.filter((currency) => currency.id === beneficiary?.currencyId)[0]
        ?.symbol;
      return fullBNEObject
        ? { ...beneficiary, amount: 0, symbol: currencySymbol }
        : {
          currency: {
            symbol: currencySymbol,
          },
          amount: 0,
        };
    }
  };

  const allowedBeneficiaries = useMemo(() => {
    const beneficiariesWithAvailableAMount = beneficiaries?.map((beneficiary: any) => {
      const relevantCurrency = companyBalancesList?.filter(
        (balance: any) => balance?.currency?.id === beneficiary?.currencyId
      )[0];
      return getBNECurrencySymbol(relevantCurrency, beneficiary, true);
    });
    return beneficiariesWithAvailableAMount;
  }, [beneficiaries]);

  function formatValue(value: string, key: string) {
    switch (key) {
      case 'countryId':
      case 'bankAccountCountryId':
        return _.has(Countries, value) ? _.get(Countries[value][0], 'name') : '';
      case 'currencyId':
        return _.has(Currencies, value) ? _.get(Currencies[value][0], 'name') : '';
        break;
      default:
        return value;
    }
  }

  const currentCurrencyBalance = useMemo(() => {
    if (id && beneficiaries?.length && companyBalancesList?.length) {
      const beneficiary = beneficiaries?.filter((beneficiary: any) => beneficiary?.id === parseInt(id))[0];
      const relevantCurrency = companyBalancesList?.filter(
        (balance: any) => balance?.currency?.id === beneficiary?.currencyId
      )[0];
      return getBNECurrencySymbol(relevantCurrency, beneficiary, false);
    }
  }, [id, beneficiaries, companyBalancesList]);

  return (
    <Box component="form" noValidate>
      <FormCard>
        <FormHeader1
          title={t('page.pay.create.step.1.beneficiary_details.beneficiary_details.title')}
          text={t('page.pay.create.step.1.beneficiary_details.beneficiary_details.text')}
        />
        <Stack spacing={2}>
          <FormCreator
            initialValidation={props.invalidFields}
            name="localPayment"
            handleChange={(event) => {
              if(event.target.value === undefined){
                dispatch(
                        upsertPayBeneficiary({
                          beneficiary: undefined,
                          beneficiaryId: undefined
                        })
                      );
                navigate(`/${pagesEnum.PAY}/new`);
              }
              else{
                navigate(`/${pagesEnum.PAY}/new/${event.target.value}`);
              }
            
            }}
            fieldsGroups={[
              {
                type: fieldsGroupType.col,
                fields: [
                  {
                    type: fieldType.select,
                    valueProperty: 'id',
                    labelProperty: 'nickname',
                    props: {
                      sx: {
                        display: 'inline-block',
                        '& .MuiInputBase-root': {
                          '&:before': {
                            position: 'absolute',
                            display: currentCurrencyBalance ? 'block' : 'none',
                            content:
                              '"' +
                              currentCurrencyBalance?.currency?.symbol +
                              getFormattedNumber(currentCurrencyBalance?.amount) +
                              '"',
                            height: '24px',
                            lineHeight: '24px',
                            borderRadius: '16px',
                            backgroundColor: 'rgba(145, 158, 171, 0.16)',
                            padding: '0 8px',
                            right: '60px',
                          },
                        },
                      },
                      required: true,
                      name: PayEnum.beneficiaryId,
                      label: t(
                        `page.pay.create.step.1.beneficiary_details.beneficiary_details.beneficiary_name.text`
                      ),
                      value: selectedValue,
                      options: _.values(allowedBeneficiaries),
                    },
                    renderOption: (props, option) => {
                      return (
                        <Box
                          component="li"
                          disabled={true}
                          {...props}
                          className=""
                          sx={{
                            padding: '8px 16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography>{option.label}</Typography>
                          <Chip size="small" label={option.symbol + getFormattedNumber(option.amount)} />
                        </Box>
                      );
                    },
                  },
                ],
              },
            ]}
          />
          <Collapse in={_.isObject(pay.beneficiary)}>
            <LightGreenCard>
              <Stack spacing={2}>
                <Paragraph3
                  title={t('page.pay.create.step.1.beneficiary_details.beneficiary_summary.title')}
                />
                <Stack direction={{ sm: 'row', xs: 'column' }} spacing={{ sm: 5, xs: 2 }}>
                  <Stack spacing={2}>
                    {BeneficiaryDetailsBankAccount
                    .filter((field) => _.get(pay.beneficiary || '', field))
                    .map((field) => {
                      return (
                        <Paragraph5
                          key={field}
                          //color={theme.palette.root.basicDeepBlue}
                          title={t(prefixFields + _.snakeCase(field) + '.text')}
                          text={formatValue(_.get(pay.beneficiary || '', field) || '', field)}
                        />
                      );
                    })}
                  </Stack>
                  <Stack spacing={2}>
                    {_.keys(paymentsMethodsMapping)
                    .map((field) => {
                      return !_.isNil(_.get(pay.beneficiary, paymentsMethodsMapping[field])) &&
                        _.includes(
                          paymentsMethodsDisplayList,
                          paymentsMethodsMapping[field]
                        ) ? (
                        <Paragraph5
                          key={field}
                          //color={theme.palette.root.basicDeepBlue}
                          title={t(prefixFields + _.snakeCase(field) + '.text')}
                          text={_.get(pay.beneficiary, paymentsMethodsMapping[field]) || ''}
                        />
                      ) : (
                        <Fragment key={field}></Fragment>
                      );
                    })}
                  </Stack>
                </Stack>
              </Stack>
            </LightGreenCard>
          </Collapse>
          <CenteredText>
            <Typography>
              {t('page.pay.create.step.1.beneficiary_details.beneficiary_details.not_exist.text')}&nbsp;
              <CustomLink
                onClick={() => {
                  dispatch(togglePage(`/${pagesEnum.BENEFICIARY}/new`));
                }}
              >
                {t(
                  'page.pay.create.step.1.beneficiary_details.beneficiary_details.create_new_link.text'
                )}
              </CustomLink>
            </Typography>
          </CenteredText>
        </Stack>
      </FormCard>
    </Box>
  );
}
